import React, { useState } from 'react';
import PropTypes from 'prop-types';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import {
  FDConfirmationDialog,
  FDPaper, FDSecondaryActionButton, FDStack, FDTitleText,
} from '@facet-wealth/design';
import { MetaResourceRenderer } from 'Components/UI/Meta/MetaForm/Components/MetaResourceRenderer';
import {
  GuidedHouseholdReviewGoalListSuggestion,
} from 'Views/Page/Content/Templates/guidedHouseholdReviewGoalListPageTemplate/suggestion';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { GoalWithoutThreshold } from 'Components/UI/guidedHouseholdReview/goalWithoutThreshold';
import moment from 'moment';
import {
  GuidedHouseholdReviewGoalListFilterControls,
} from './filterControls';
import { metaOverrideSet } from './metaOverrideSet';

export const GuidedHouseholdReviewGoalListLayout = ({ refresh }) => {
  const { resource } = useMetaLayoutContext();
  const [showDismissAllConfirmation, setShowDismissAllConfirmation] = React.useState(false);
  const [dismissingSuggestedGoals, setDismissingSuggestedGoals] = React.useState(false);
  const { displayErrorMessage } = useSnackbarContext();

  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [typesToShow, setTypesToShow] = useState(new Set());

  const handleDismissAllSuggested = async () => {
    try {
      setDismissingSuggestedGoals(true);
      await Promise.all(resource.attr('suggestedGoals').resources().list().map((goal, i) => {
        return resource.attr('suggestedGoals').resources().index(i).delete();
      }));
      refresh();
    } catch (err) {
      displayErrorMessage(err, err.errors);
    } finally {
      setDismissingSuggestedGoals(false);
      setShowDismissAllConfirmation(false);
    }
  };

  let filteredGoals = resource.attr('goalList').resources().list();
  if (typesToShow.size > 0) {
    filteredGoals = filteredGoals.filter(goal => typesToShow.has(goal.attr('type')));
  }
  if (startDate) {
    filteredGoals = filteredGoals.filter(goal => (goal.attr('targetDate') ? moment(goal.attr('targetDate')).isSameOrAfter(startDate) : false));
  }
  if (endDate) {
    filteredGoals = filteredGoals.filter(goal => (goal.attr('targetDate') ? moment(goal.attr('targetDate')).isSameOrBefore(endDate) : false));
  }

  return (
    <>
      <FDStack direction={'column'} spacing={4} paddingY={4}>
        <FDPaper padding={4}>
          <FDStack direction={'column'} padding={2} spacing={2}>
            <GuidedHouseholdReviewGoalListFilterControls
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
              typesToShow={typesToShow}
              setTypesToShow={setTypesToShow}
              resource={resource}
            />
            {/*  Header Row */}
            <FDStack direction={'row'} spacing={2} justifyContent={'space-between'} alignItems={'center'}>
              <FDTitleText
                flex={4}
                size={'lg'}
                value={resource.attr('goalList').meta().field('label').attr('prompt')}
              />
              <FDTitleText
                flex={2}
                size={'lg'}
                value={resource.attr('goalList').meta().field('amount').attr('prompt')}
                align={'end'}
              />
              <FDTitleText
                flex={1}
                size={'lg'}
                value={resource.attr('goalList').meta().field('timing').attr('prompt')}
                align={'end'}
              />
            </FDStack>

            {filteredGoals.map(goal => {
              return (
                <GoalWithoutThreshold key={goal.id()} resource={goal} />
              );
            })}
          </FDStack>
        </FDPaper>
      </FDStack>


      {!resource.metaField('suggestedGoals').attr('hidden') ? (
        <FDPaper appearance={'elevated'} padding={4}>
          <FDStack direction={'column'} padding={2} spacing={2}>
            <FDTitleText
              flex={4}
              size={'lg'}
              value={'Suggested goals'}
            />

            <FDStack direction="column" spacing={2} justifyContent={'center'}>
              <FDStack direction="column" spacing={1} justifyContent={'center'}>
                {resource.attr('suggestedGoals').resources().list().map(goal => (
                  <MetaResourceRenderer
                    key={goal.id()}
                    metaOverrideSet={metaOverrideSet}
                    layout={GuidedHouseholdReviewGoalListSuggestion}
                    resource={goal}
                    LayoutProps={{ onDeleted: refresh }}
                  />
                ))}
              </FDStack>
              <FDStack direction={'row'} justifyContent={'center'}>
                <FDSecondaryActionButton
                  onClick={async () => {
                    setShowDismissAllConfirmation(true);
                  }}
                  size={'sm'}
                  label={'Dismiss all'}
                />
              </FDStack>
              <FDConfirmationDialog
                message={'Are you sure you would like to dismiss all suggested goals?'}
                title={'Dismiss all suggested goals'}
                onConfirm={handleDismissAllSuggested}
                confirmLabel={'Yes, dismiss all'}
                onClose={() => setShowDismissAllConfirmation(false)}
                open={showDismissAllConfirmation}
                loading={dismissingSuggestedGoals}
              />
            </FDStack>
          </FDStack>
        </FDPaper>
      ) : null}
    </>
  );
};

GuidedHouseholdReviewGoalListLayout.propTypes = {
  refresh: PropTypes.func.isRequired,
};
